<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import ChevronDown from '/svg/chevron-down.svg'

interface Props {
  modelValue: string | number
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: string
  searchable?: boolean
  variant?: 'default' | 'checkbox'
  appearance?: string
  options: {
    label: string
    value: string
    icon?: string
    disabled?: boolean
  }[]
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  searchable: false,
  variant: 'default',
  appearance: '',
})

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const isFocused = ref(false)
const isOpen = ref(false)
const wrapper = ref<HTMLElement | null>(null)
const searchQuery = ref('')
const selectedOption = ref(props.options.find((opt) => opt.value === props.modelValue))

onClickOutside(wrapper, () => {
  isOpen.value = false
  searchQuery.value = ''
})

const filteredOptions = computed(() => {
  if (!searchQuery.value || !props.searchable) return props.options

  return props.options.filter((option) => option.label.toLowerCase().includes(searchQuery.value.toLowerCase()))
})

function updateValue(option: { label: string; value: string | number; disabled?: boolean }) {
  if (option.disabled) {
    return
  }

  emit('update:modelValue', option.value)
  selectedOption.value = props.options.find((opt) => opt.value === option.value)

  setTimeout(() => {
    isOpen.value = false
    searchQuery.value = ''
  }, 50)
}

function toggleOpen() {
  if (!props.disabled) {
    isOpen.value = !isOpen.value
    if (isOpen.value && props.searchable) {
      setTimeout(() => {
        const searchInput = document.getElementById('search-input')
        if (searchInput) searchInput.focus()
      }, 100)
    } else {
      searchQuery.value = ''
    }
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    selectedOption.value = props.options.find((opt) => opt.value === newValue)
  }
)
</script>

<template>
  <div class="relative flex flex-col gap-1 w-full h-[71.47px]" ref="wrapper">
    <!-- Label -->
    <label
      v-if="label"
      class="w-fit bg-white px-1 transition-all z-30 duration-400 text-[#1b325a] pointer-events-none"
      :class="[
        isFocused || modelValue
          ? 'absolute ms-4 -mt-2 text-[10px] font-bold'
          : 'absolute mt-[13px] ms-5 text-[13px] font-medium',
        appearance === 'admin' ? 'text-[#6C6C6C]' : 'text-[#1b325a]',
      ]"
    >
      {{ label }}
    </label>

    <!-- Custom Select Wrapper -->
    <div class="relative w-full h-12">
      <!-- Selected Value Display -->
      <div
        class="w-full h-full rounded-xl border px-[18px] pr-8 text-[13px] font-medium outline-none transition border-[#E0E0E0] focus:outline-none focus:ring-0 focus:border-[#000] bg-white flex items-center"
        :class="[
          disabled
            ? 'cursor-not-allowed text-[#ABBAD5]'
            : 'text-[#1A1A1A] cursor-pointer',
        ]"
        @click="toggleOpen"
      >
        <span class="flex items-center gap-2">
          <img
            v-if="selectedOption?.icon"
            :src="selectedOption.icon"
            alt="icon"
            class="w-4 h-4"
          />
          <template v-if="selectedOption">
            {{ selectedOption.label }}
          </template>
          <template v-else-if="props.placeholder">
            {{ props.placeholder }}
          </template>
        </span>
      </div>

      <!-- Chevron Icon -->
      <img
        :src="ChevronDown"
        alt="chevron"
        class="absolute top-1/2 end-0.5 -translate-y-1/2 w-6 h-6 pointer-events-none transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
      />

      <!-- Custom Dropdown Options -->
      <Transition name="fade-slide" appear>
        <div
          v-if="isOpen"
          class="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E0E0E0] rounded-xl shadow-lg z-[999] max-h-60 overflow-y-auto custom-dropdown"
        >
          <!-- Search Input (only when searchable is true) -->
          <div
            v-if="searchable"
            class="sticky flex items-center top-0 bg-white p-2 border-b border-[#E0E0E0]"
          >
            <input
              id="search-input"
              type="text"
              v-model="searchQuery"
              placeholder="جستجو..."
              class="w-full ps-8 py-2 text-sm border border-[#E0E0E0] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1b325a]"
              @keydown.esc="
                isOpen = false;
                searchQuery = '';
              "
            />

            <svg
              class="absolute start-4"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.66853 1.83398C7.58193 1.83387 8.47666 2.09258 9.24905 2.58013C10.0214 3.06768 10.6399 3.76411 11.0327 4.58873C11.4255 5.41335 11.5766 6.3324 11.4685 7.23937C11.3604 8.14635 10.9975 9.00413 10.4219 9.71332L13.6885 12.98C13.7782 13.0691 13.8308 13.1889 13.8358 13.3152C13.8407 13.4415 13.7976 13.565 13.7151 13.6609C13.6326 13.7567 13.5169 13.8177 13.3913 13.8317C13.2656 13.8456 13.1393 13.8114 13.0379 13.736L12.9819 13.6873L9.71453 10.4207C9.11379 10.9081 8.40482 11.2442 7.64721 11.4007C6.88961 11.5572 6.1055 11.5296 5.36078 11.3202C4.61606 11.1107 3.9325 10.7256 3.36753 10.1971C2.80256 9.66867 2.37269 9.01231 2.11406 8.28322C1.85542 7.55413 1.77557 6.7736 1.88122 6.00725C1.98687 5.24089 2.27493 4.51108 2.72119 3.87917C3.16745 3.24725 3.75888 2.73169 4.44576 2.3758C5.13265 2.01991 5.89492 1.8341 6.66853 1.83398ZM6.66853 2.83398C6.16513 2.83398 5.66666 2.93314 5.20158 3.12578C4.7365 3.31842 4.31391 3.60078 3.95795 3.95674C3.602 4.3127 3.31964 4.73528 3.12699 5.20036C2.93435 5.66545 2.8352 6.16392 2.8352 6.66732C2.8352 7.17072 2.93435 7.66919 3.12699 8.13427C3.31964 8.59935 3.602 9.02194 3.95795 9.37789C4.31391 9.73385 4.7365 10.0162 5.20158 10.2089C5.66666 10.4015 6.16513 10.5007 6.66853 10.5007C7.68519 10.5007 8.66022 10.0968 9.37911 9.37789C10.098 8.659 10.5019 7.68398 10.5019 6.66732C10.5019 5.65065 10.098 4.67563 9.37911 3.95674C8.66022 3.23785 7.68519 2.83398 6.66853 2.83398Z"
                fill="#1A1A1A"
              />
            </svg>
          </div>

          <!-- Options List -->
          <div v-if="filteredOptions.length > 0">
            <div
              v-for="opt in filteredOptions"
              :key="opt.value"
              class="px-4 py-3 transition-all duration-200 text-[15px] font-medium flex items-center gap-2"
              :class="[
                opt.disabled
                  ? 'cursor-not-allowed opacity-60'
                  : 'cursor-pointer',
                props.variant === 'default'
                  ? opt.value === modelValue
                    ? 'bg-[#DAE9F8] text-[#1b325a] font-bold'
                    : 'text-[#1b325a] hover:bg-[#f8f9fa]'
                  : 'text-[#1b325a]',
              ]"
              @click="updateValue(opt)"
            >
              <template v-if="props.variant === 'checkbox'">
                <div
                  class="w-5 h-5 rounded-md border-[2.5px] flex items-center justify-center duration-200"
                  :class="
                    opt.value === modelValue
                      ? 'border-[#2d5396] bg-[#2d5396]'
                      : 'border-[#858585]'
                  "
                >
                  <svg
                    v-if="opt.value === modelValue"
                    class="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3"
                    viewBox="0 0 24 24"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </template>

              <!-- <img v-if="opt.icon" :src="opt.icon" alt="icon" class="w-4 h-4 shrink-0" /> -->
              <!-- {{ opt.label }} -->
              <!-- <img v-if="opt.disabled" src="/svg/ic_warning.svg" width="16" height="24"
                                    alt="warning-icon" /> -->

              <span>
                {{ opt.label }}
              </span>

              <span v-if="opt.disabled" class="text-[9px] text-gray-400">
                (احراز هویت نشده)
              </span>
            </div>
          </div>
          <div v-else class="px-4 py-3 text-[15px] text-gray-500 text-center">
            موردی یافت نشد
          </div>
        </div>
      </Transition>
    </div>

    <!-- Hidden Native Select for Form Submission -->
    <select :disabled="disabled" :value="modelValue" class="hidden">
      <option v-for="opt in options" :key="opt.value" :value="opt.value">
        {{ opt.label }}
      </option>
    </select>

    <!-- Error -->
    <!-- <div
      v-if="error"
      class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1"
    >
      <img src="/svg/error-icon.svg" width="16" alt="error-icon" />{{ error }}
    </div> -->
  </div>
</template>

<style scoped>
/* Custom scrollbar for dropdown */
.custom-dropdown::-webkit-scrollbar {
  width: 6px;
}

.custom-dropdown::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease-out;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-6px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
